/**
 * One tenancy, in the M1a reference layout.
 *
 * WHAT THIS REPLACES. The read-only half of `TenancyCard.js` — 539 lines of
 * inline JSX that rendered a summary header, nine flat blocks and an eight-item
 * accordion, in a fourth visual dialect, with the status derived from the
 * viewer's device clock. This renders the same information as a header, a KPI
 * row and five cards, taking every value from the server.
 *
 * WHAT IT DOES NOT TOUCH. Every write path is a child component that arrives
 * unchanged: `RentalContractSection`, `HandoverSection` (and through it the key
 * receipt and inspection modals), `TenancyDocumentsSection`,
 * `DamageClaimsSection`, `TenancyParticipantMutationsSection` and, since
 * KND-2, `TenancyTerminationSection`. This file re-presents their entry
 * points; it does not re-implement their state, their requests or their
 * dialogs.
 *
 * REQUESTS. Five of those children fetch on mount. That is why only the
 * expanded tenancy renders them: a collapsed tenancy costs nothing, where
 * previously every tenancy on the page fetched six times whether or not the
 * reader ever opened this tab.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatDateOnly } from "../../../../utils/datetime";
import {
  Card,
  EmptyState,
  Field,
  Pairs,
  SECONDARY_BUTTON,
  show,
} from "../../tenancies/workspaceUi";
import DamageClaimsSection from "../DamageClaimsSection";
import HandoverSection from "../HandoverSection";
import RentalContractSection from "../RentalContractSection";
import TenancyDocumentsSection from "../TenancyDocumentsSection";
import TenancyParticipantMutationsSection from "../TenancyParticipantMutationsSection";
import TenancyTerminationSection from "../termination/TenancyTerminationSection";
import { canEditDepositPayout } from "../overview/DepositPayoutModal";
import { formatChfRent } from "../tenantDetailHelpers";
import {
  CalendarIcon,
  FileIcon,
  FolderIcon,
  HomeIcon,
  IconTile,
  KeyIcon,
  KpiRow,
  KpiTile,
  ShieldIcon,
  StatusPill,
  UsersIcon,
  WalletIcon,
} from "./tenancyCardUi";
import {
  depositTypeLabelDe,
  depositView,
  isFixedTerm,
  monthlyView,
  moveInProtocolState,
  partiesView,
  tenancyHeaderSegments,
  tenancyPeriodIso,
  tenancyStatusView,
  viewerRoleLabel,
} from "./tenancyViewSelectors";

const dash = (iso) => (iso ? formatDateOnly(iso) : "—");

/** CHF, or an em dash — never "CHF 0.00" for a figure nobody entered. */
const chf = (n) => (n == null ? null : formatChfRent(n));

/* ------------------------------------------------------------------ header - */

function TenancyHeader({ tn, roleLabel, onEdit, onOpenUnit, editDisabled }) {
  const segments = tenancyHeaderSegments(tn);
  const status = tenancyStatusView(tn);
  const period = tenancyPeriodIso(tn);

  return (
    <div className="flex flex-wrap items-start gap-3">
      <IconTile tone="blue" size="lg">
        <HomeIcon />
      </IconTile>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3
            className="m-0 min-w-0 text-[17px] font-semibold tracking-tight text-foreground"
            data-testid="tenancy-header-label"
          >
            {segments.length ? segments.join(" · ") : "Mietverhältnis"}
          </h3>
          <StatusPill tone={status.tone} testId="tenancy-status-pill">
            {status.label}
          </StatusPill>
          {isFixedTerm(tn) ? (
            <StatusPill tone="sky" testId="tenancy-fixed-term-pill">
              Befristet
            </StatusPill>
          ) : null}
          {roleLabel ? (
            <StatusPill tone="slate" testId="tenancy-viewer-role">
              {roleLabel}
            </StatusPill>
          ) : null}
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CalendarIcon />
            <span data-testid="tenancy-period">
              {dash(period.from)} – {dash(period.to)}
            </span>
          </span>
          {tn?.reference_number ? (
            <span data-testid="tenancy-reference">
              Referenz:{" "}
              <span className="font-mono text-[12px] text-foreground">
                {tn.reference_number}
              </span>
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onEdit}
          disabled={editDisabled}
          className={SECONDARY_BUTTON}
          data-testid="tenancy-edit-button"
        >
          Bearbeiten
        </button>
        {tn?.unit_id ? (
          <button
            type="button"
            onClick={onOpenUnit}
            disabled={editDisabled}
            className={SECONDARY_BUTTON}
          >
            Zur Einheit
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- kpi - */

function TenancyKpis({ tn, deposit, documentCount, inspectionsList }) {
  const monthly = monthlyView(tn);
  const protocol = moveInProtocolState(inspectionsList, tn?.id);

  return (
    <KpiRow>
      <KpiTile
        testId="tenancy-kpi-monthly"
        tone="blue"
        icon={<WalletIcon />}
        label="Monatlich wiederkehrend"
        value={chf(monthly)}
        // Named for what the figure is: the server sums the tenancy's active
        // recurring revenue lines. Calling it "Grundmiete" would be wrong as
        // soon as a second recurring position exists.
        hint="Summe aktiver Positionen"
      />
      <KpiTile
        testId="tenancy-kpi-deposit"
        tone="emerald"
        icon={<ShieldIcon />}
        label="Kaution"
        value={chf(deposit.amount)}
        hint={depositTypeLabelDe(deposit.type) || undefined}
      />
      <KpiTile
        testId="tenancy-kpi-documents"
        tone="violet"
        icon={<FolderIcon />}
        label="Dokumente"
        value={documentCount == null ? null : String(documentCount)}
        hint="im Mietverhältnis"
      />
      <KpiTile
        testId="tenancy-kpi-handover"
        tone="amber"
        icon={<KeyIcon />}
        // Names the one step it can prove, rather than the reference layout's
        // "Einzug abgeschlossen: Ja" — nothing defines move-in completeness.
        label="Übergabeprotokoll"
        value={protocol.label}
        hint={protocol.date ? formatDateOnly(protocol.date) : undefined}
      />
    </KpiRow>
  );
}

/* ------------------------------------------------------------------ parties - */

/**
 * One person on the contract.
 *
 * The name is a link to that person's own tenant page whenever the roster gave
 * us their id — a co-tenant is a tenant, and `/admin/tenants/{id}` is the link
 * every other surface already uses for one (AdminUnitDetailPage, RoomDetailCards,
 * the deposit modal). It navigates and nothing more: the id is the one the
 * org-scoped tenancy read already returned, so no party costs a request here.
 *
 * A party with no id — the legacy branch of `partiesView` when a tenancy names
 * no contract holder — stays plain text rather than becoming a dead link.
 */
function PartyName({ party }) {
  if (!party.tenantId || !party.name) {
    return (
      <span className="truncate text-[13px] text-foreground">
        {show(party.name)}
      </span>
    );
  }
  return (
    <Link
      to={`/admin/tenants/${encodeURIComponent(party.tenantId)}`}
      className="truncate text-[13px] text-sky-700 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
    >
      {party.name}
    </Link>
  );
}

function PartyRow({ party }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <span className="flex min-w-0 items-center gap-2">
        <PartyName party={party} />
        <StatusPill tone={party.isPrimary ? "emerald" : "sky"}>
          {party.role}
        </StatusPill>
        {!party.active ? (
          <StatusPill tone="slate">
            {party.validTo ? `bis ${formatDateOnly(party.validTo)}` : "Beendet"}
          </StatusPill>
        ) : null}
      </span>
      <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
        {party.email ? <span className="break-all">{party.email}</span> : null}
        {party.phone ? <span>{party.phone}</span> : null}
      </span>
    </li>
  );
}

/* ------------------------------------------------------------ collapsed row - */

/**
 * A tenancy the reader has not opened.
 *
 * Deliberately renders no child section, which is the whole point: the heavy
 * children fetch on mount, so a collapsed tenancy must cost zero requests. It
 * still carries everything needed to choose one — object, status, period,
 * monthly figure and the viewer's role.
 */
export function TenancySummaryRow({ tn, viewerTenantId, expanded, onToggle }) {
  const segments = tenancyHeaderSegments(tn);
  const status = tenancyStatusView(tn);
  const period = tenancyPeriodIso(tn);
  const roleLabel = viewerRoleLabel(tn, viewerTenantId);
  const monthly = monthlyView(tn);

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      data-testid={`tenancy-summary-${tn?.id}`}
      className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-[10px] border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="truncate text-[13.5px] font-medium text-foreground">
          {segments.length ? segments.join(" · ") : "Mietverhältnis"}
        </span>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        {isFixedTerm(tn) ? <StatusPill tone="sky">Befristet</StatusPill> : null}
        {roleLabel ? <StatusPill tone="slate">{roleLabel}</StatusPill> : null}
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
        <span>
          {dash(period.from)} – {dash(period.to)}
        </span>
        <span className="tabular-nums">{chf(monthly) ?? "—"}</span>
        <span className="text-foreground">
          {expanded ? "Schliessen" : "Öffnen"}
        </span>
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------- main - */

export default function TenancyDetailCard({
  tn,
  tenant,
  viewerTenantId,
  documentCount,
  onDocumentCountChange,
  onEdit,
  onOpenUnit,
  // K4d: the deposit's own editors, opened for THIS tenancy. The card holds no
  // deposit state and derives nothing new — it hands `tn` back up, and the page
  // decides which row the one mounted modal is looking at.
  onEditDeposit,
  onEditDepositPayout,
  editDisabled = false,
  inspectionsList,
  inspectionDetailById,
  openOrCreateInspection,
  onParticipantsChanged,
  revenueSummary = null,
  activityFeed = null,
  // KND-2: the termination block inside the Mietvertrag card. The cap is the
  // unit's head-lease end (a hint + input max on the termination date);
  // `onTerminationChanged` is the page's reload of rows + Verlauf.
  terminationMaxIso = null,
  onTerminationChanged,
  // M1c-E: the page's "a signature just finished" signal, shaped
  // `{ tenancyId, nonce }`. It is page-level because the inspection modal is:
  // it is rendered by AdminTenantDetailPage, not inside this card. Several
  // cards can be expanded at once (`expandedTenancyIds` is a Set), so the
  // signal names its tenancy and every other card ignores it — one signature
  // must not cost a document request in three places.
  documentsRefreshSignal = null,
}) {
  // One counter for both paths. A key receipt is signed inside this card and
  // reports upward; an inspection is signed in the page's modal and arrives
  // through the signal above. Either way the tenancy's document list is stale
  // and this is what makes it ask again.
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);
  const bumpDocumentsRefresh = useCallback(
    () => setDocumentsRefreshKey((v) => v + 1),
    []
  );

  const signalNonce = documentsRefreshSignal?.nonce ?? 0;
  const signalTenancyId = documentsRefreshSignal?.tenancyId ?? null;
  useEffect(() => {
    // nonce 0 is "nothing has been signed yet", not a signal.
    if (!signalNonce) return;
    if (String(signalTenancyId) !== String(tn?.id)) return;
    bumpDocumentsRefresh();
  }, [signalNonce, signalTenancyId, tn?.id, bumpDocumentsRefresh]);

  const deposit = depositView(tn);
  const parties = partiesView(tn, tenant);

  /*
    K4d — the deposit's two actions, for THIS tenancy.

    NAMED BY REFERENCE. Several of these cards are open at once during an
    internal move, so "Kaution bearbeiten" on its own would be the same
    accessible name three times on one page. The reference number is what the
    card already prints beside the period, so it is what the label uses;
    unit/room and finally the id stand in when a row has none.

    HIERARCHY, unchanged from K4x.3c: editing the deposit is the bordered
    action, recording how a returned one was settled is the quiet one beside
    it. `PRIMARY_BUTTON` is deliberately NOT used — this workspace reserves it
    for submit, and the card's siblings ("Bearbeiten", "Zur Einheit") are all
    SECONDARY_BUTTON.

    AVAILABILITY, unchanged from K4x.3c: the payout action appears when the
    security was returned, or when payout details are already stored (the
    repair door for a row whose flag was never set). `canEditDepositPayout`
    IS that rule — restating it here would give it a second, drifting copy.
  */
  const depositLabel =
    tn?.reference_number ||
    [tn?.unit_title, tn?.room_name].filter(Boolean).join(" · ") ||
    tn?.id ||
    "";
  const depositActions =
    onEditDeposit || onEditDepositPayout ? (
      /*
        BELOW THE FACTS, NOT IN THE CARD HEADER — measured, not chosen.

        The header is `flex items-start justify-between gap-3` with no wrap and
        a `shrink-0` action, so at 375px the two buttons (123px + 118px) took
        the whole row and squeezed the "Kaution" title to a measured width of
        0: the card lost its own name. Below the facts both buttons keep their
        full width at every breakpoint, and the same DOM serves desktop — no
        breakpoint-specific ownership for a difference that does not need one.
      */
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        {onEditDeposit ? (
          <button
            type="button"
            onClick={() => onEditDeposit(tn)}
            disabled={editDisabled}
            className={SECONDARY_BUTTON}
            aria-label={`Kaution für Mietverhältnis ${depositLabel} bearbeiten`}
            data-testid={`tenancy-deposit-edit-${tn?.id}`}
          >
            Kaution bearbeiten
          </button>
        ) : null}
        {onEditDepositPayout && canEditDepositPayout(tn) ? (
          <button
            type="button"
            onClick={() => onEditDepositPayout(tn)}
            disabled={editDisabled}
            aria-label={`Rückzahlungsdetails für Mietverhältnis ${depositLabel} öffnen`}
            data-testid={`tenancy-deposit-payout-${tn?.id}`}
            className="shrink-0 rounded-[6px] px-1 py-0.5 text-[12px] text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Rückzahlungsdetails
          </button>
        ) : null}
      </div>
    ) : null;
  const roleLabel = viewerRoleLabel(tn, viewerTenantId);

  return (
    <div
      className="flex flex-col gap-4"
      data-testid={`tenancy-detail-${tn?.id}`}
    >
      <TenancyHeader
        tn={tn}
        roleLabel={roleLabel}
        onEdit={onEdit}
        onOpenUnit={onOpenUnit}
        editDisabled={editDisabled}
      />

      <TenancyKpis
        tn={tn}
        deposit={deposit}
        documentCount={documentCount}
        inspectionsList={inspectionsList}
      />

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
        <Card
          title="Mietvertrag"
          icon={
            <IconTile tone="blue">
              <FileIcon />
            </IconTile>
          }
          testId="tenancy-card-contract"
        >
          <RentalContractSection tenancyId={tn?.id} />
          <TenancyTerminationSection
            tn={tn}
            terminationMaxIso={terminationMaxIso}
            onChanged={onTerminationChanged}
          />
        </Card>

        <Card
          title="Einzug & Übergabe"
          icon={
            <IconTile tone="amber">
              <KeyIcon />
            </IconTile>
          }
          testId="tenancy-card-handover"
        >
          <HandoverSection
            tenancy={tn}
            inspectionsList={inspectionsList}
            inspectionDetailById={inspectionDetailById}
            openOrCreateInspection={openOrCreateInspection}
            onKeyReceiptSigned={bumpDocumentsRefresh}
          />
        </Card>

        <Card
          title="Kaution"
          icon={
            <IconTile tone="emerald">
              <ShieldIcon />
            </IconTile>
          }
          // K4d, superseding K4x.3b's note here: this card now owns the
          // deposit's edit entry. K4x.3b was right that the HEADER's
          // "Bearbeiten" must not pretend to edit a deposit — it edits the
          // contract — and that is still true. What changed is that the
          // Übersicht is no longer the place either: it can only ever show one
          // tenancy, so its single action silently picked one of several and
          // was absent entirely for reserved, cancelled and ended contracts.
          // These two actions are the deposit's own, aimed at THIS `tn`.
          testId="tenancy-card-deposit"
        >
          <Pairs>
            <Field label="Betrag" value={chf(deposit.amount)} />
            <Field label="Typ" value={depositTypeLabelDe(deposit.type)} />
            <Field
              label="Eingegangen"
              value={
                deposit.received
                  ? deposit.receivedAt
                    ? `Ja · ${formatDateOnly(deposit.receivedAt)}`
                    : "Ja"
                  : "Nein"
              }
            />
            <Field
              label="Zurückbezahlt"
              value={
                deposit.returned
                  ? deposit.returnedAt
                    ? `Ja · ${formatDateOnly(deposit.returnedAt)}`
                    : "Ja"
                  : "Nein"
              }
            />
            {deposit.type === "insurance" ? (
              <Field label="Anbieter" value={deposit.provider} wide />
            ) : null}
          </Pairs>
          {depositActions}
        </Card>

        <Card
          title="Dokumente im Mietverhältnis"
          icon={
            <IconTile tone="violet">
              <FolderIcon />
            </IconTile>
          }
          testId="tenancy-card-documents"
        >
          <TenancyDocumentsSection
            tenancyId={tn?.id}
            onCountChange={onDocumentCountChange}
            hideHeading
            refreshKey={documentsRefreshKey}
          />
        </Card>

        <Card
          title="Mieter & Co-Mieter"
          icon={
            <IconTile tone="slate">
              <UsersIcon />
            </IconTile>
          }
          testId="tenancy-card-parties"
        >
          {parties.length ? (
            <ul className="m-0 list-none divide-y divide-border p-0">
              {parties.map((p) => (
                <PartyRow key={p.id} party={p} />
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Keine Parteien erfasst"
              testId="tenancy-parties-empty"
            />
          )}
          <div className="mt-3">
            <TenancyParticipantMutationsSection
              tenancyId={tn?.id}
              tenancy={tn}
              participants={tn?.participants}
              onChanged={onParticipantsChanged}
            />
          </div>
        </Card>

        <Card
          title="Schadenfälle"
          icon={
            <IconTile tone="rose">
              <ShieldIcon />
            </IconTile>
          }
          testId="tenancy-card-claims"
        >
          <DamageClaimsSection tenancyId={tn?.id} hideHeading />
        </Card>
      </div>

      {/*
        Finanzen, Aufgaben, Rapporte and Aktivität keep their previous places
        behind a disclosure. They are per-tenancy detail rather than part of the
        at-a-glance answer the reference layout gives, and three of them mount
        their own fetches — collapsed, they cost nothing.
      */}
      {revenueSummary || activityFeed ? (
        <Accordion type="multiple" defaultValue={[]} className="w-full">
          {revenueSummary ? (
            <AccordionItem value="finanzen" className="border-border">
              <AccordionTrigger className="text-[12px] font-semibold uppercase tracking-[0.6px] text-muted-foreground hover:no-underline">
                Finanzen
              </AccordionTrigger>
              <AccordionContent>{revenueSummary}</AccordionContent>
            </AccordionItem>
          ) : null}
          {activityFeed ? (
            <AccordionItem value="weiteres" className="border-border">
              <AccordionTrigger className="text-[12px] font-semibold uppercase tracking-[0.6px] text-muted-foreground hover:no-underline">
                Aufgaben, Rapporte & Aktivität
              </AccordionTrigger>
              <AccordionContent>{activityFeed}</AccordionContent>
            </AccordionItem>
          ) : null}
        </Accordion>
      ) : null}
    </div>
  );
}
